import React from "react";
import { observer } from "mobx-react-lite";
import { Text, Stack, Divider, HStack, Box } from "@chakra-ui/react";
import { useStore } from "@/store";
import { OrderUnitItem } from "./OrderUnitItem";
import type { OrderProductData } from "@/store/types/order";
import { translations } from "@/constants/translations";
import { ProductCategorySelect } from "./ProductCategorySelect";
import { PromoCodePanel } from "./PromoCodePanel";

export const OrderUnitDetails = observer(() => {
  const {
    activeLanguage,
    orderProducts,
    productCategories,
    orderData,
    orderTotals,
    isOntarioFlow,
    isIndigenousFlow,
    setOrderProductAmount,
  } = useStore();

  const language = translations.pages["order-details"][activeLanguage];

  const handleQuantityChange = (product: OrderProductData, amount: number) => {
    setOrderProductAmount(product.id, amount);
  };

  if (productCategories.length === 0) return null;

  return (
    <Stack spacing="8">
      {productCategories.length > 1 && (
        <HStack width="100%">
          <Text>Category: </Text>
          <ProductCategorySelect />
        </HStack>
      )}

      {orderProducts.map((product) => (
        <OrderUnitItem
          key={product.name}
          product={product}
          onQuantityChange={(value) => handleQuantityChange(product, value)}
          subtotalLabel={language.subtotal}
          showSubtotal={!isOntarioFlow}
        />
      ))}

      <HStack justifyContent="space-between">
        <Box width={{ base: "0", md: "372px" }} />

        <Stack
          flex="1"
          spacing="8"
        >
          {!isIndigenousFlow && (
            <>
              <Divider />
              <Stack>
                <PromoCodePanel
                  ml="auto"
                  colorScheme="light"
                />
                {orderData.promotionalCode && (
                  <Text
                    color="primary-light"
                    ml="auto"
                  >
                    Promotional code applied successfully
                  </Text>
                )}
              </Stack>
              {orderData.promotionalCode && (
                <>
                  <Stack ml="auto">
                    <HStack>
                      <Text
                        minWidth="80px"
                        textAlign="right"
                      >
                        Order:
                      </Text>
                      <Text fontWeight="bold">
                        {orderTotals.formatted.subtotal}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text
                        minWidth="80px"
                        textAlign="right"
                      >
                        Discount:
                      </Text>
                      <Text fontWeight="bold">
                        {orderTotals.formatted.discount}
                      </Text>
                    </HStack>
                  </Stack>
                </>
              )}
            </>
          )}

          <Divider />

          <HStack justifyContent="flex-end">
            <HStack>
              <Text>{language.order_subtotal}</Text>
              <Text fontWeight="bold">
                {orderTotals.formatted.subtotalWithDiscount}
              </Text>
            </HStack>
          </HStack>
          {orderData.tax > 0 && (
            <HStack justifyContent="flex-end">
              <HStack>
                <Text>{language.tax}</Text>
                <Text fontWeight="bold">{orderTotals.formatted.tax}</Text>
              </HStack>
            </HStack>
          )}
        </Stack>
      </HStack>
    </Stack>
  );
});
